import { ToggleGroup, ToggleGroupItem } from "../toggle-group";
import { Label } from "../label";

import { getFieldError } from "./get-field-error";
import { useFieldContext } from "./form";

interface ToggleGroupOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface FormToggleGroupProps extends Omit<
  React.ComponentProps<typeof ToggleGroup>,
  "value" | "onValueChange" | "type" | "defaultValue"
> {
  label?: string;
  options: ToggleGroupOption[];
  showErrorMessage?: boolean;
}

function FormToggleGroup({
  label,
  options,
  showErrorMessage = true,
  ...props
}: FormToggleGroupProps) {
  const field = useFieldContext<string>();

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <Label htmlFor={field.name} className="text-sm font-medium">
          {label}
        </Label>
      ) : null}
      <ToggleGroup
        type="single"
        value={field.state.value ?? ""}
        variant="outline"
        onValueChange={(value) => {
          if (value) {
            field.handleChange(value);
          }
        }}
        {...props}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {showErrorMessage && field.state.meta.errors.length > 0 ? (
        <span className="text-sm text-destructive">
          {getFieldError(field.state.meta.errors[0])}
        </span>
      ) : null}
    </div>
  );
}

interface FormToggleGroupMultipleProps extends Omit<
  React.ComponentProps<typeof ToggleGroup>,
  "value" | "onValueChange" | "type" | "defaultValue"
> {
  label?: string;
  options: ToggleGroupOption[];
  showErrorMessage?: boolean;
}

function FormToggleGroupMultiple({
  label,
  options,
  showErrorMessage = true,
  ...props
}: FormToggleGroupMultipleProps) {
  const field = useFieldContext<string[]>();

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <Label htmlFor={field.name} className="text-sm font-medium">
          {label}
        </Label>
      ) : null}
      <ToggleGroup
        type="multiple"
        value={field.state.value ?? []}
        onValueChange={(value) => field.handleChange(value)}
        {...props}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      {showErrorMessage && field.state.meta.errors.length > 0 ? (
        <span className="text-sm text-destructive">
          {getFieldError(field.state.meta.errors[0])}
        </span>
      ) : null}
    </div>
  );
}

export { FormToggleGroup, FormToggleGroupMultiple };
export type { ToggleGroupOption };
